import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import "../Details.css";
import {
  useSearchParams,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import Navbar from "../navbar";
import Chart from "react-apexcharts";
import "./BarChart.css";
import ChartTable from "./ChartTable.js";
import ChartFiltersPanel from "./ChartFiltersPanel";
import * as tablesApi from "../../services/tablesApi";
import { useAuth } from "../../auth/AuthContext";
import { openPrintWindow, buildFiltersSummaryHtml } from "./printChartTemplate";
import { busyErrorFrom, DataApiBusyError, useDataApiBusy } from "../../services/dataApiBusy";
import BusyNotice from "./BusyNotice";

function BarChart({
  title,
  chartProperties,
  height,
  width,
  hasAdvancedOptions,
  fetchEmployees,
  overrideChartCategory,
  overrideChartID,
  periodSelection,
  hideSecondCategory,
  onEmployeeFilterChange,
  defaultEmployeeFilter = [],
  overrideChartType,
  summaryExpandedByDefault = true,
  // (serverOptions, serverSeries) => { options, series, height? } — see homeChartPresets.js
  preset,
  // Dashboard tile: no filter/print buttons, preset owns the title
  compact = false,
}) {
  const pageTitle = "Data";

  const location = useLocation();
  const { categoryID, chartID } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  const handleFiltersCollapsedChange = (isCollapsed) => {
    setFiltersCollapsed(isCollapsed);
    // ApexCharts keeps the width it measured in the narrow column.
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  };

  const { accessToken, tenantServerUrl, user, selectedRestaurantId, viewAsEmail } =
    useAuth();
  // Prompt debug view: TableMate administrators, or a platform admin viewing as a user from Control
  const canSeePrompt = String(user?.role || "").toLowerCase() === "admin" || Boolean(viewAsEmail);
  const RestaurantID = selectedRestaurantId;

  const printRef = useRef(null); // 🔹 main chart area to print

  console.log("[BAR CHART] URL: " + tenantServerUrl);
  console.log("[BAR CHART] RestaurantID: " + selectedRestaurantId); //use first restaurant ID
  console.log("[BAR CHART] User: " + JSON.stringify(user));
  console.log("[BAR CHART] ACCESS TOKEN: " + JSON.stringify(accessToken));
  console.log(
    "[BAR CHART] Page Route: dcid:" + categoryID + " /  chid:" + chartID
  );
  console.log(
    "[BAR CHART] Period selection in BarChart (prop):",
    periodSelection
  );
  console.log("[BarChart] tenantServerUrl:", tenantServerUrl);
  console.log("[BarChart] RestaurantID:", RestaurantID);

  const [content_data, setData] = useState(null);

  console.log("Properties: " + JSON.stringify(chartProperties));

  const hasChartProps =
    !!chartProperties &&
    !!chartProperties.options &&
    !!chartProperties.series;

  const [chartOptions, setChartOptions] = useState(() =>
    hasChartProps ? chartProperties.options : { chart: { type: "line" } }
  );

  const [chartSeries, setChartSeries] = useState(() =>
    hasChartProps ? chartProperties.series : []
  );

  // 🔑 This forces a brand-new <Chart> instance when incremented
  const [chartInstanceKey, setChartInstanceKey] = useState(0);
  const [presetHeight, setPresetHeight] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const [selectedEmployees, setSelectedEmployees] = useState(
    () => defaultEmployeeFilter
  );
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [menuCategories, setMenuCategories] = useState([]);
  const [menuCatLoading, setMenuCatLoading] = useState(false);
  const [menuCatError, setMenuCatError] = useState(null);

  const MENU_CATEGORIES_API = useMemo(() => {
    if (!tenantServerUrl) return null;
    return `${tenantServerUrl.replace(
      /\/$/,
      ""
    )}/tablemate/endpoints/menu/fetchAllMenuSubCategoriesBESA.php`;
  }, [tenantServerUrl]);

  const REDIR_BASE = "https://mydata.tablemate.net/redir.php?url=";

  // Fetch menu subcategories for ChartFiltersPanel (tenant-aware)
  useEffect(() => {
    if (!MENU_CATEGORIES_API) return;

    let alive = true;

    (async () => {
      try {
        setMenuCatLoading(true);
        setMenuCatError(null);

        const proxiedUrl = `${REDIR_BASE}${encodeURIComponent(
          MENU_CATEGORIES_API
        )}`;

        const res = await fetch(proxiedUrl, {
          headers: { Accept: "application/json" },
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const json = await res.json();
        console.log("Menu categories raw response:", json);

        if (!json || json.ok !== true || !Array.isArray(json.categories)) {
          throw new Error("Invalid categories payload");
        }

        const normalized =
          json.categories
            .map((c) => {
              const subcode =
                c.subcode ??
                c.id ??
                c.categoryID ??
                c.menu_category_id ??
                c.category_id ??
                c.menu_categoryID ??
                null;

              const main =
                c.main ??
                c.name ??
                c.title ??
                c.category_name ??
                c.menu_category_name ??
                null;

              if (!subcode && !main) return null;

              return {
                value: String(subcode),
                label: main || `Category ${subcode}`,
                raw: c,
              };
            })
            .filter(Boolean) || [];

        if (alive) setMenuCategories(normalized);
      } catch (err) {
        console.error("Failed to load menu categories:", err);
        if (alive) setMenuCatError("Failed to load menu categories");
      } finally {
        if (alive) setMenuCatLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [MENU_CATEGORIES_API, REDIR_BASE]);

  const catLabelToValue = useMemo(() => {
    const m = new Map();
    menuCategories.forEach((c) => {
      m.set(c.label, c.value);
    });
    return m;
  }, [menuCategories]);

  // date filter mapping
  const formatYMD = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}`;
  };
  const formatYM = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  };

  const resolvedPeriod = periodSelection || {
    mode: "today",
    start: null,
    end: null,
  };
  const mode = resolvedPeriod.mode;
  const start = resolvedPeriod.start;
  const end = resolvedPeriod.end;

  const {
    chartPeriod,
    chartDateSelected,
    fromParam,
    toParam,
    periodLabel,
  } = useMemo(() => {
    console.log("Recomputing period mapping from selection:", {
      mode,
      start,
      end,
    });

    const fmtShort = (d) =>
      d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });

    const fmtMonthYearLabel = (d) =>
      d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
      });

    let p = "3";
    let dVal = "";
    let from = "";
    let to = "";
    const todayNow = new Date();
    const formattedDateTodayNow = formatYMD(todayNow);
    let label = `Today - ${formattedDateTodayNow}`;

    if (!start || !end) {
      // Default to client's local today with explicit date params
      return {
        chartPeriod: "5",
        chartDateSelected: formattedDateTodayNow,
        fromParam: formattedDateTodayNow,
        toParam: formattedDateTodayNow,
        periodLabel: label,
      };
    }

    from = formatYMD(start);
    to = formatYMD(end);

    const now = new Date();
    const isCurrentMonth =
      start.getFullYear() === now.getFullYear() &&
      start.getMonth() === now.getMonth();

    const isCurrentYear = start.getFullYear() === now.getFullYear();

    switch (mode) {
      case "month":
        p = "1";
        dVal = formatYM(start);
        label = isCurrentMonth
          ? `This Month - ${fmtMonthYearLabel(start)}`
          : fmtMonthYearLabel(start);
        break;

      case "year":
        p = "2";
        dVal = String(start.getFullYear());
        label = isCurrentYear
          ? `This Year - ${String(start.getFullYear())}`
          : String(start.getFullYear());
        break;

      case "today": {
        // Use explicit local date (p=5) so the analyse API matches the
        // restaurant's calendar day, not the UTC server clock.
        const todayLocal = start || new Date();
        const formattedDateToday = formatYMD(todayLocal);
        p = "5";
        dVal = formattedDateToday;
        from = formattedDateToday;
        to = formattedDateToday;
        label = `Today - ${formattedDateToday}`;
        break;
      }

      case "range":
        p = "4";
        label = `${fmtShort(start)} – ${fmtShort(end)}`;
        break;

      case "date":
        p = "5";
        dVal = from;
        label = fmtShort(start);
        break;

      default:
        p = "0";
        label = "All Time";
        break;
    }

    return {
      chartPeriod: p,
      chartDateSelected: dVal,
      fromParam: from,
      toParam: to,
      periodLabel: label,
    };
  }, [mode, start, end]);

  const chartFilter = useMemo(
    () =>
      selectedEmployees && selectedEmployees.length
        ? selectedEmployees.join(",")
        : "",
    [selectedEmployees]
  );

  const menufilter = useMemo(
    () =>
      selectedCategories && selectedCategories.length
        ? selectedCategories.join(",")
        : "",
    [selectedCategories]
  );

  const [chartType, setChartType] = useState(() => {
    if (hasChartProps) {
      return chartProperties.options?.chart?.type || "line";
    }
    if (overrideChartCategory == null) {
      return localStorage.getItem("chartType") || "line";
    }
    return "bar";
  });

  useEffect(() => {
    if (hasChartProps) return;

    if (overrideChartCategory == null) {
      const stored = localStorage.getItem("chartType");
      if (stored && stored !== chartType) {
        setChartType(stored);
      }
    } else {
      if (chartType !== "bar") setChartType("bar");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, overrideChartCategory, hasChartProps]);

  useEffect(() => {
    if (!hasChartProps && overrideChartCategory == null && chartType) {
      localStorage.setItem("chartType", chartType);
    }
  }, [chartType, overrideChartCategory, hasChartProps]);

  const effectiveType =
    overrideChartType === "table"
      ? "table"
      : hasChartProps
      ? chartProperties.options?.chart?.type || "line"
      : overrideChartCategory == null
      ? chartType || "line"
      : "bar";

  const isTable = effectiveType === "table";

  console.log(
    "search parameters (debug): f:" +
      chartFilter +
      " menf:" +
      menufilter +
      " p:" +
      chartPeriod +
      " d:" +
      chartDateSelected +
      " from:" +
      fromParam +
      " to:" +
      toParam
  );

  const analyseUrl =
    `https://data.tablemate.net/analyse` +
    `?rid=${encodeURIComponent(RestaurantID)}` +
    `&cid=${encodeURIComponent(categoryID || overrideChartCategory || "1")}` +
    `&chid=${encodeURIComponent(chartID || overrideChartID || "1")}` +
    `&p=${encodeURIComponent(chartPeriod)}` +
    `&f=${encodeURIComponent(chartFilter)}` +
    `&d=${encodeURIComponent(chartDateSelected)}` +
    `&from=${encodeURIComponent(fromParam)}` +
    `&to=${encodeURIComponent(toParam)}` +
    `&menf=${encodeURIComponent(menufilter)}` +
    `&t=${encodeURIComponent(effectiveType)}` +
    `&exp=raw`;

  const url = analyseUrl;
  console.log("URL " + url);

  const { busy, setBusy, attempt: busyAttempt, retry: retryBusy } = useDataApiBusy(url);

  // Logical identity of the chart (based on params/filters)
  const chartKey = `chart-${
    categoryID || overrideChartCategory || "1"
  }-${chartID || overrideChartID || "1"}-${effectiveType}-${fromParam}-${toParam}-${chartPeriod}-${chartDateSelected}-${chartFilter}-${menufilter}`;

  // If static chartProperties are provided, keep chartOptions/series in sync
  useEffect(() => {
    if (!hasChartProps || !chartProperties) return;

    const { series = [], options = {} } = chartProperties || {};

    // hard reset local state for this new static chart
    setChartOptions(options || { chart: { type: effectiveType } });
    setChartSeries(Array.isArray(series) ? [...series] : []);

    // ✅ Force a fresh Apex instance
    setChartInstanceKey((k) => k + 1);
  }, [chartProperties, hasChartProps, effectiveType]);

  useEffect(() => {
    // If chart props are passed in, don't fetch
    if (hasChartProps) {
      console.log("Skipping fetch: chartProperties provided.");
      return;
    }

    // Don't hit the API until we actually have a token
    if (!accessToken) {
      console.log("Skipping fetch: no accessToken yet.");
      return;
    }

    let alive = true;
    const controller = new AbortController();

    console.log("Fetching chart data for URL:", url);

    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          console.error("Fetch error status:", response.status);
          throw (await busyErrorFrom(response)) || new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((content_data) => {
        if (!alive) return;
        setBusy(null);

        console.log("Results:", content_data);
        console.log("Just got data for chart type: " + effectiveType);

        // 🔄 First, clear state completely
        setChartOptions({ chart: { type: effectiveType } });
        setChartSeries([]);

        if (!isTable && typeof preset === "function") {
          const { series = [], ...options } = content_data || {};
          const shaped = preset(options, Array.isArray(series) ? series : []);
          setChartOptions(shaped.options);
          setChartSeries(shaped.series);
          setPresetHeight(shaped.height || null);
        } else if (!isTable) {
          const { series = [], ...options } = content_data || {};

          // Subtitle text is overridden at render with the local periodLabel
          const nextOptions = {
            ...(options || {}),
            chart: {
              ...(options?.chart || {}),
              type: effectiveType,
            },
          };

          setChartOptions(nextOptions);
          setChartSeries(Array.isArray(series) ? [...series] : []);

          const serverType = options?.chart?.type;
          if (serverType && serverType !== effectiveType) {
            setChartType(serverType);
          }

          console.log("series ", series);
          console.log("options ", JSON.stringify(options));
          console.log("Type: ", serverType);
        } else {
          setChartSeries(content_data);
          console.log(
            "This is a table chart with data: " +
              JSON.stringify(content_data)
          );
        }

        // ✅ Force a brand-new ApexCharts instance for this dataset
        setChartInstanceKey((k) => k + 1);
        setIsLoaded(true);
      })
      .catch((error) => {
        if (!alive) return;
        console.error("Error fetching data:", error);
        if (error instanceof DataApiBusyError) {
          setBusy(error);
          return;
        }
        if (typeof preset === "function") {
          setChartOptions((o) => ({ ...o, noData: { ...(o.noData || {}), text: "Couldn't load this chart" } }));
          setChartSeries([]);
          setIsLoaded(true);
        }
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [url, effectiveType, isTable, hasChartProps, accessToken, preset, busyAttempt, setBusy]);

  const restaurantId = RestaurantID;

  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [empError, setEmpError] = useState(false);

  // Fetch employees (tenant-aware)
  useEffect(() => {
    if (!tenantServerUrl) return;

    let alive = true;
    (async () => {
      try {
        setEmpLoading(true);
        setEmpError(false);

        let raw = [];
        if (typeof fetchEmployees === "function") {
          raw = await fetchEmployees({ restaurantId });
        } else if (
          tablesApi &&
          typeof tablesApi.listEmployees === "function"
        ) {
          raw = await tablesApi.listEmployees(tenantServerUrl, {
            restaurantId,
          });
        } else {
          console.warn(
            "No employees fetcher found. Provide `fetchEmployees` prop or implement tablesApi.listEmployees()."
          );
        }

        const normalized =
          (raw || [])
            .map((e) => {
              const id =
                e?.id ??
                e?.employee_id ??
                e?.user_id ??
                e?.username ??
                e?.email ??
                e?.name;
              const username = e?.username ?? e?.email ?? id;

              const display =
                e?.display_name ??
                e?.name ??
                e?.fullname ??
                username ??
                String(id ?? "");

              const label =
                username && display && username !== display
                  ? `${display} (${username})`
                  : display || username || String(id ?? "");

              return {
                value: String(display || username || id || ""),
                label,
              };
            })
            .filter((x) => x.value) || [];

        if (alive) setEmployees(normalized);
      } catch (err) {
        console.error("Failed to load employees:", err);
        if (alive) setEmpError(true);
      } finally {
        if (alive) setEmpLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [restaurantId, fetchEmployees, tenantServerUrl]);

  const waiterOptions = useMemo(() => {
    const existing = new Map(employees.map((o) => [o.value, o]));
    return Array.from(existing.values());
  }, [employees]);

  const [ChartTitle, setChartTitle] = useState("Loading...");
  const [docChart, setProjectCategories] = useState(false);

  const [showFilterInfo, setShowFilterInfo] = useState(false);

  const selectedCategoryDetails = useMemo(
    () =>
      selectedCategories.map((subcode) => {
        const found = menuCategories.find(
          (c) => c.value === String(subcode)
        );
        return found
          ? `${found.label} (subcode: ${found.value})`
          : String(subcode);
      }),
    [selectedCategories, menuCategories]
  );

  // Executive summary: derive insights from chart data for business storytelling
  const executiveSummary = useMemo(() => {
    const fmtNum = (n) => {
      if (n == null || Number.isNaN(n)) return "—";
      if (Number.isInteger(n)) return n.toLocaleString();
      return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    if (isTable) {
      const rows = Array.isArray(chartSeries) ? chartSeries : [];
      if (rows.length === 0) {
        return {
          title: "Executive Summary",
          paragraphs: [
            "No table data available for the selected period and filters. Adjust the date range or filters to see data.",
          ],
        };
      }
      const first = rows[0];
      const keys = typeof first === "object" && first !== null ? Object.keys(first) : [];
      const numericKeys = keys.filter((k) => {
        const v = first[k];
        return typeof v === "number" || (typeof v === "string" && /^-?[\d.]+$/.test(v));
      });
      const totalRows = rows.length;
      const sums = {};
      numericKeys.forEach((k) => {
        let s = 0;
        rows.forEach((r) => {
          const v = r[k];
          const n = typeof v === "number" ? v : parseFloat(v);
          if (!Number.isNaN(n)) s += n;
        });
        sums[k] = s;
      });
      const lines = [
        `This table contains ${totalRows} row${totalRows !== 1 ? "s" : ""} for the period ${periodLabel}.`,
        numericKeys.length
          ? `Key numeric totals: ${numericKeys.map((k) => `${k}: ${fmtNum(sums[k])}`).join("; ")}.`
          : "Review the table for detailed breakdowns.",
      ];
      return { title: "Executive Summary", paragraphs: lines };
    }

    const series = Array.isArray(chartSeries) ? chartSeries : [];
    const categories = chartOptions?.xaxis?.categories || [];
    if (series.length === 0 && !categories.length) {
      return {
        title: "Executive Summary",
        paragraphs: [
          "No chart data available for the selected period and filters. Adjust the date range or filters to see data.",
        ],
      };
    }

    const insights = [];
    const seriesNames = series.map((s) => s?.name ?? "Series").filter(Boolean);
    const dataByCategory = (categories.length ? categories : []).map((cat, i) => ({
      category: cat,
      index: i,
      values: series.map((s) => {
        const d = s?.data;
        if (Array.isArray(d)) {
          const point = d[i];
          return typeof point === "number" ? point : (point && point[1] != null ? point[1] : null);
        }
        return null;
      }),
    }));

    const totalsPerSeries = series.map((s, idx) => {
      const d = s?.data;
      if (!Array.isArray(d)) return 0;
      return d.reduce((acc, point) => {
        const v = typeof point === "number" ? point : (point && point[1] != null ? point[1] : 0);
        return acc + (Number.isFinite(v) ? v : 0);
      }, 0);
    });

    const overallTotal = totalsPerSeries.reduce((a, b) => a + b, 0);
    const dataPointCount = series.reduce((acc, s) => acc + (Array.isArray(s?.data) ? s.data.length : 0), 0);

    insights.push(
      `Over **${periodLabel}**, this ${effectiveType} chart presents ${series.length} metric${series.length !== 1 ? "s" : ""}${seriesNames.length ? `: ${seriesNames.slice(0, 5).join(", ")}${seriesNames.length > 5 ? "…" : ""}` : ""}.`
    );

    if (dataByCategory.length > 0 && overallTotal !== 0) {
      const categoryTotals = dataByCategory.map(({ category, values }) => ({
        category,
        total: values.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0),
      }));
      categoryTotals.sort((a, b) => b.total - a.total);
      const top = categoryTotals[0];
      const bottom = categoryTotals[categoryTotals.length - 1];
      if (top && top.total > 0) {
        insights.push(
          `**Leading segment** is "${top.category}" with a total of ${fmtNum(top.total)}${series.length > 1 ? " across all metrics" : ""}.`
        );
        if (bottom && bottom.category !== top.category && bottom.total < top.total) {
          insights.push(
            `The lowest segment is "${bottom.category}" (${fmtNum(bottom.total)}), offering room to grow in that area.`
          );
        }
      }
    }

    if (totalsPerSeries.length > 0) {
      const maxSeriesTotal = Math.max(...totalsPerSeries);
      const maxIdx = totalsPerSeries.indexOf(maxSeriesTotal);
      const maxSeriesName = seriesNames[maxIdx] || `Metric ${maxIdx + 1}`;
      insights.push(
        `**${maxSeriesName}** has the highest aggregate value (${fmtNum(maxSeriesTotal)}), driving the bulk of the story for this view.`
      );
    }

    const avgPerPoint = dataPointCount > 0 && overallTotal !== 0
      ? overallTotal / dataPointCount
      : 0;
    if (avgPerPoint > 0) {
      insights.push(
        `Average value per data point is ${fmtNum(avgPerPoint)}, giving a sense of typical performance across the period.`
      );
    }

    insights.push(
      "Use these insights to align staffing, promotions, or menu focus with high-performing segments and to improve underperforming areas."
    );

    return {
      title: "Executive Summary",
      paragraphs: insights,
    };
  }, [
    chartSeries,
    chartOptions,
    periodLabel,
    effectiveType,
    isTable,
  ]);

  // Toggle: basic (rule-based) vs LLM-generated summary
  const [summaryMode, setSummaryMode] = useState("basic"); // 'basic' | 'llm'
  const [summaryExpanded, setSummaryExpanded] = useState(summaryExpandedByDefault);
  const [llmSummary, setLlmSummary] = useState(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmError, setLlmError] = useState(null);
  const [showPromptDebug, setShowPromptDebug] = useState(false);

  // Prompt the Data API built for the last summary (debug view)
  const [llmPrompt, setLlmPrompt] = useState(null);

  // Invalidate LLM summary when chart data/filters change
  useEffect(() => {
    setLlmSummary(null);
    setLlmError(null);
    setLlmPrompt(null);
  }, [chartKey]);

  // The Data API builds the prompt and calls Groq/Gemini; the keys stay on the server
  const fetchLlmSummary = useCallback(async () => {
    setLlmLoading(true);
    setLlmError(null);
    try {
      const payload = {
        restaurantId: RestaurantID,
        periodLabel,
        chartType: effectiveType,
        isTable,
        title: title || null,
        ...(isTable
          ? { tableRows: Array.isArray(chartSeries) ? chartSeries : [] }
          : {
              series: (chartSeries || []).map((s) => ({
                name: s?.name ?? "Series",
                data: s?.data ?? [],
              })),
              categories: chartOptions?.xaxis?.categories ?? [],
            }),
      };

      const res = await fetch("https://data.tablemate.net/chart-summary", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });
      const busy = await busyErrorFrom(res);
      if (busy) throw busy;
      const data = await res.json().catch(() => ({}));
      if (data?.prompt) setLlmPrompt(data.prompt);
      if (!res.ok) {
        throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
      }
      setLlmSummary(typeof data?.summary === "string" ? data.summary : "");
    } catch (err) {
      setLlmError(err?.message || "Failed to generate AI summary.");
      setLlmSummary(null);
    } finally {
      setLlmLoading(false);
    }
  }, [
    accessToken,
    RestaurantID,
    periodLabel,
    effectiveType,
    isTable,
    title,
    chartSeries,
    chartOptions,
  ]);

  // Scroll to #chart when arriving via chart-nav hash links
  useEffect(() => {
    if (location.hash !== "#chart") return;
    const el = document.getElementById("chart");
    if (!el) return;
    // Wait a tick so layout/chart mount before scrolling
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [location.hash, location.pathname, chartKey]);

  // 🔹 PRINT HANDLER – uses shared template (printChartTemplate.js)
  const handlePrint = () => {
    if (!printRef.current) return;

    const summaryItems = [
      `<li><strong>Restaurant:</strong> ${RestaurantID}</li>`,
      `<li><strong>Category (cid):</strong> ${
        categoryID || overrideChartCategory || "1"
      }</li>`,
      `<li><strong>Chart (chid):</strong> ${
        chartID || overrideChartID || "1"
      }</li>`,
      `<li><strong>Chart Type:</strong> ${effectiveType}</li>`,
      `<li><strong>Period Label:</strong> ${periodLabel}</li>`,
      `<li><strong>Period Params:</strong> p=${chartPeriod}, d=${
        chartDateSelected || '""'
      }, from=${fromParam || '""'}, to=${toParam || '""'}</li>`,
      `<li><strong>Employees (f):</strong> ${
        selectedEmployees.length ? selectedEmployees.join(", ") : "All"
      }</li>`,
      `<li><strong>Menu Filter (menf – subcodes):</strong> ${
        selectedCategories.length ? selectedCategories.join(", ") : "All"
      }</li>`,
      `<li><strong>Menu Categories (main + subcode):</strong> ${
        selectedCategoryDetails.length
          ? selectedCategoryDetails.join(" | ")
          : "All"
      }</li>`,
    ];

    openPrintWindow({
      title: title || "Chart",
      contentHtml: printRef.current.innerHTML,
      summaryHtml: buildFiltersSummaryHtml(summaryItems),
      user,
      selectedRestaurantId,
    });
  };



  const mixedChartClass = [
    "mixed-chart",
    hasAdvancedOptions ? "mixed-chart--with-filters" : "",
    hasAdvancedOptions && filtersCollapsed ? "is-filters-collapsed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="chart-container">
      <div id="chart" className={mixedChartClass}>
        <div className="mixed-chart-main" ref={printRef}>
          {!compact && (
          <div className="chart-header-row">
            <div>
              {/* <h2 className="chart-title">{title}</h2> */}
              <p className="chart-period">Period: {periodLabel}</p>
            </div>

            <div className="chart-meta-actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowFilterInfo((v) => !v)}
              >
                {showFilterInfo
                  ? "Hide Filters & Parameters"
                  : "View Filters & Parameters"}
              </button>

              {/* 🔹 New Print button – does NOT affect layout behavior otherwise */}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ms-2"
                onClick={handlePrint}
              >
                Print Chart
              </button>
            </div>
          </div>
          )}

          {!compact && showFilterInfo && (
            <div className="chart-filters-summary">
              <h5>Applied Filters & Parameters</h5>
              <ul>
                <li>
                  <strong>Restaurant:</strong> {RestaurantID}
                </li>
                <li>
                  <strong>Category (cid):</strong>{" "}
                  {categoryID || overrideChartCategory || "1"}
                </li>
                <li>
                  <strong>Chart (chid):</strong>{" "}
                  {chartID || overrideChartID || "1"}
                </li>
                <li>
                  <strong>Chart Type:</strong> {effectiveType}
                </li>
                <li>
                  <strong>Period Label:</strong> {periodLabel}
                </li>
                <li>
                  <strong>Period Params:</strong>{" "}
                  {`p=${chartPeriod}, d=${
                    chartDateSelected || '""'
                  }, from=${fromParam || '""'}, to=${toParam || '""'}`}
                </li>
                <li>
                  <strong>Employees (f):</strong>{" "}
                  {selectedEmployees.length
                    ? selectedEmployees.join(", ")
                    : "All"}
                </li>
                <li>
                  <strong>Menu Filter (menf – subcodes):</strong>{" "}
                  {selectedCategories.length
                    ? selectedCategories.join(", ")
                    : "All"}
                </li>
                <li>
                  <strong>Menu Categories (main + subcode):</strong>{" "}
                  {selectedCategoryDetails.length
                    ? selectedCategoryDetails.join(" | ")
                    : "All"}
                </li>
              </ul>
            </div>
          )}

          {busy && !hasChartProps ? (
            <BusyNotice
              message={busy.message}
              onRetry={retryBusy}
              minHeight={Math.min(presetHeight || height, 220)}
            />
          ) : !isTable && preset ? (
            isLoaded && !chartSeries.length ? (
              <div className="chart-empty" style={{ minHeight: Math.min(presetHeight || height, 220) }}>
                <h3 className="chart-empty-title">{chartOptions.title?.text || title}</h3>
                <p className="chart-empty-text">{chartOptions.noData?.text || "No data for this period"}</p>
              </div>
            ) : isLoaded ? (
              <Chart
                key={`${chartKey}-${chartInstanceKey}`}
                options={{
                  ...chartOptions,
                  chart: {
                    ...(chartOptions.chart || {}),
                    id: `${chartKey}-${chartInstanceKey}`,
                  },
                }}
                series={chartSeries}
                type={chartOptions.chart?.type || "bar"}
                width={width}
                height={presetHeight || height}
              />
            ) : (
              <div className="chart-loading" style={{ height: presetHeight || height }}>
                Loading…
              </div>
            )
          ) : !isTable ? (
            <Chart
              key={`${chartKey}-${chartInstanceKey}`}
              options={{
                ...chartOptions,
                chart: {
                  ...(chartOptions.chart || {}),
                  id: `${chartKey}-${chartInstanceKey}`, // 🔥 unique per dataset
                  type: effectiveType,
                },
                subtitle: {
                  ...(chartOptions.subtitle || {}),
                  text: `Period - ${periodLabel}, Employee - ${
                    selectedEmployees?.length
                      ? selectedEmployees.length <= 2
                        ? selectedEmployees.join(", ")
                        : `${selectedEmployees.length} employees`
                      : "All"
                  }`,
                },
              }}
              series={chartSeries}
              type={effectiveType}
              width={width}
              height={height}
            />
          ) : (
            <ChartTable data={chartSeries} title={title} />
          )}

          <div className={`chart-executive-summary-wrapper ${!summaryExpanded ? "chart-summary-minimized" : ""}`}>
            <div
              className="chart-summary-mode-toggle"
              onClick={() => setSummaryExpanded((e) => !e)}
              role="button"
              aria-expanded={summaryExpanded}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              <span className="chart-summary-mode-label">Summary:</span>
              <button
                type="button"
                className={`chart-summary-mode-btn ${summaryMode === "basic" ? "active" : ""}`}
                onClick={(e) => { e.stopPropagation(); setSummaryMode("basic"); }}
              >
                Basic
              </button>
              <button
                type="button"
                className={`chart-summary-mode-btn ${summaryMode === "llm" ? "active" : ""}`}
                onClick={(e) => { e.stopPropagation(); setSummaryMode("llm"); }}
              >
                AI-generated
              </button>
              <span className="chart-summary-expand-icon" aria-hidden="true">
                {summaryExpanded ? " ▼" : " ▶"}
              </span>
            </div>

            {summaryExpanded && summaryMode === "basic" && executiveSummary.paragraphs.length > 0 && (
              <div className="chart-executive-summary">
                <h5 className="chart-executive-summary-title">
                  {executiveSummary.title}
                </h5>
                <div className="chart-executive-summary-body">
                  {executiveSummary.paragraphs.map((text, i) => {
                    const parts = text.split(/\*\*(.*?)\*\*/g);
                    return (
                      <p key={i} className="chart-executive-summary-p">
                        {parts.map((part, j) =>
                          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                        )}
                      </p>
                    );
                  })}
                </div>
              </div>
            )}

            {summaryExpanded && summaryMode === "llm" && (
              <div className="chart-executive-summary chart-executive-summary-llm">
                <h5 className="chart-executive-summary-title">
                  Executive Summary (AI)
                </h5>
                <p className="chart-ai-disclaimer" role="status">
                  AI-generated content. Verify figures and insights before making business decisions.
                </p>
                {canSeePrompt && (
                  <div className="chart-prompt-debug">
                    <button
                      type="button"
                      className="chart-prompt-debug-toggle"
                      onClick={() => setShowPromptDebug((v) => !v)}
                    >
                      {showPromptDebug ? "Hide prompt" : "Show prompt"} (debug)
                    </button>
                    {showPromptDebug && (
                      <pre className="chart-prompt-debug-content">
                        {llmPrompt || "Generate a summary to see the prompt sent to the AI."}
                      </pre>
                    )}
                  </div>
                )}
                <div className="chart-executive-summary-body">
                  {llmLoading && (
                    <p className="chart-executive-summary-p">
                      <span className="chart-llm-loading">Generating summary…</span>
                    </p>
                  )}
                  {!llmLoading && llmError && (
                    <>
                      <p className="chart-executive-summary-p chart-llm-error">
                        {llmError}
                      </p>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={fetchLlmSummary}
                      >
                        Retry
                      </button>
                    </>
                  )}
                  {!llmLoading && !llmError && llmSummary && (
                    llmSummary.split(/\n\n+/).map((para, i) => (
                      <p key={i} className="chart-executive-summary-p">
                        {para.split(/\*\*(.*?)\*\*/g).map((part, j) =>
                          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                        )}
                      </p>
                    ))
                  )}
                  {!llmLoading && !llmError && !llmSummary && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary chart-llm-generate-btn"
                      onClick={fetchLlmSummary}
                    >
                      Generate AI summary
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {hasAdvancedOptions && (
          <aside className="mixed-chart-filters">
            <ChartFiltersPanel
              employees={waiterOptions}
              categories={menuCategories.map((c) => c.label)}
              defaultScope="granular"
              hideSecondCategory={hideSecondCategory}
              defaultEmployees={selectedEmployees}
              storageKey={`filters-${selectedRestaurantId}-cat${categoryID}-q${chartID}`}
              collapsed={filtersCollapsed}
              onCollapsedChange={handleFiltersCollapsedChange}
              onToggleCollapsed={() =>
                handleFiltersCollapsedChange(!filtersCollapsed)
              }
              onApply={({ scope, employees, categories }) => {
                console.log("ChartFiltersPanel onApply:", {
                  scope,
                  employees,
                  categories,
                });

                const empValues = (employees || [])
                  .map((e) =>
                    typeof e === "string"
                      ? e
                      : e.value ?? e.name ?? e.label ?? null
                  )
                  .filter(Boolean);

                const catValues = (categories || [])
                  .map((label) => catLabelToValue.get(label) || label)
                  .filter(Boolean);

                setSelectedEmployees(empValues);
                setSelectedCategories(catValues);

                if (typeof onEmployeeFilterChange === "function") {
                  onEmployeeFilterChange(empValues);
                }
              }}
            />
            {menuCatError && (
              <p style={{ color: "red", marginTop: "0.5rem" }}>
                {menuCatError}
              </p>
            )}
          </aside>
        )}
      </div>

      <style>{`
        .chart-header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          flex-wrap: wrap;
        }
        .chart-meta-actions {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        }
        .chart-filters-summary {
          margin: 8px 0 12px;
          padding: 8px 10px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
          font-size: 13px;
        }
        .chart-filters-summary h5 {
          margin: 0 0 6px;
          font-size: 14px;
          font-weight: 600;
        }
        .chart-filters-summary ul {
          margin: 0;
          padding-left: 18px;
        }
        .chart-filters-summary li + li {
          margin-top: 4px;
        }
        .chart-executive-summary-wrapper {
          margin-top: 12px;
        }
        .chart-empty {
          display: flex;
          flex-direction: column;
        }
        .chart-empty-title {
          margin: 0;
          font-size: 15px;
          font-weight: 600;
          color: #111827;
        }
        .chart-empty-text {
          margin: auto;
          color: #9ca3af;
          font-size: 13px;
        }
        .chart-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          font-size: 13px;
        }
        .chart-executive-summary-wrapper.chart-summary-minimized .chart-summary-mode-toggle {
          padding: 6px 10px;
          margin-bottom: 0;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          background: #f8fafc;
          font-size: 12px;
        }
        .chart-executive-summary-wrapper.chart-summary-minimized .chart-summary-expand-icon {
          margin-left: auto;
          color: #6b7280;
          font-size: 10px;
        }
        .chart-executive-summary {
          margin-top: 12px;
          padding: 10px 12px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          background: #f8fafc;
          font-size: 12px;
          line-height: 1.45;
        }
        .chart-executive-summary-title {
          margin: 0 0 6px;
          font-size: 12px;
          font-weight: 700;
        }
        .chart-executive-summary-p {
          margin: 0 0 6px;
          font-size: 11px;
        }
        .chart-executive-summary-p:last-child {
          margin-bottom: 0;
        }
        .chart-summary-mode-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 8px;
        }
        .chart-summary-mode-label {
          font-weight: 600;
          font-size: 12px;
        }
        .chart-summary-mode-btn {
          padding: 2px 8px;
          font-size: 11px;
          border-radius: 4px;
          border: 1px solid #d1d5db;
          background: #fff;
          cursor: pointer;
        }
        .chart-summary-mode-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
          color: #fff;
        }
        .btn.btn-sm.btn-outline-secondary {
          font-size: 12px;
          padding: 4px 8px;
          border-radius: 999px;
          border: 1px solid #9ca3af;
          background: #fff;
          cursor: pointer;
        }
        .btn.btn-sm.btn-outline-secondary:hover {
          background: #f3f4f6;
        }
      `}</style>
    </div>
  );
}

export default BarChart;
